import apiClient from '../axios.js'

/**
 * 系統帳號管理 API
 */
export const systemAccountService = {
    /**
     * 獲取系統帳號列表
     * @param {Object} params - 查詢參數
     * @param {number} params.page - 頁碼
     * @param {number} params.pageSize - 每頁數量
     * @param {string} params.search - 搜尋關鍵字
     * @returns {Promise<Object>} 帳號列表
     */
    getAccounts: async (params = {}) => {
        const response = await apiClient.get('/system/accounts', { params })
        return response
    },

    /**
     * 獲取單一系統帳號詳情
     * @param {string} id - 帳號 ID
     * @returns {Promise<Object>} 帳號詳情
     */
    getAccount: async (id) => {
        const response = await apiClient.get(`/system/accounts/${id}`)
        return response
    },

    /**
     * 新增系統帳號
     * @param {Object} accountData - 帳號資料
     * @param {string} accountData.username - 使用者名稱
     * @param {string} accountData.password - 密碼
     * @param {string} accountData.email - Email
     * @param {string} accountData.name - 姓名
     * @param {string} accountData.role - 角色（可選，預設 system_admin）
     * @param {string} accountData.status - 狀態（可選，預設 active）
     * @param {boolean} accountData.hasAllPermissions - 是否有全部權限（可選，預設 false）
     * @param {Array<string>} accountData.permissions - 權限列表（可選，預設 []）
     * @returns {Promise<Object>} 新增結果
     */
    createAccount: async (accountData) => {
        const response = await apiClient.post('/system/accounts', accountData)
        return response
    },

    /**
     * 更新系統帳號
     * @param {string} id - 帳號 ID
     * @param {Object} accountData - 帳號資料
     * @returns {Promise<Object>} 更新結果
     */
    updateAccount: async (id, accountData) => {
        const response = await apiClient.put(`/system/accounts/${id}`, accountData)
        return response
    },

    /**
     * 刪除系統帳號
     * @param {string} id - 帳號 ID
     * @returns {Promise<Object>} 刪除結果
     */
    deleteAccount: async (id) => {
        const response = await apiClient.delete(`/system/accounts/${id}`)
        return response
    },

    /**
     * 重設系統帳號密碼（管理員功能）
     * @param {string} id - 帳號 ID
     * @param {Object} data - 密碼資料
     * @param {string} data.newPassword - 新密碼
     * @returns {Promise<Object>} 重設結果
     */
    resetPassword: async (id, data) => {
        const response = await apiClient.put(`/system/accounts/${id}/password/reset`, data)
        return response
    },

    /**
     * 獲取單個系統帳號的狀態變更歷史
     * @param {string} id - 帳號 ID
     * @param {Object} params - 查詢參數
     * @param {number} params.page - 頁碼
     * @param {number} params.pageSize - 每頁數量
     * @returns {Promise<Object>} 狀態變更歷史
     */
    getStatusHistory: async (id, params = {}) => {
        const response = await apiClient.get(`/system/accounts/${id}/status-history`, { params })
        return response
    },

    /**
     * 獲取所有系統帳號的狀態變更歷史
     * @param {Object} params - 查詢參數
     * @param {number} params.page - 頁碼
     * @param {number} params.pageSize - 每頁數量
     * @param {string} params.accountId - 帳號 ID（可選）
     * @param {string} params.startDate - 開始時間（可選）
     * @param {string} params.endDate - 結束時間（可選）
     * @param {string} params.oldStatus - 舊狀態（可選）
     * @param {string} params.newStatus - 新狀態（可選）
     * @returns {Promise<Object>} 狀態變更歷史
     */
    getAllStatusHistory: async (params = {}) => {
        const response = await apiClient.get('/system/accounts/status-history', { params })
        return response
    },
}
